import { useEffect, useState } from "react";
import { fetchWaitlist, joinWaitlist, type WaitlistEntry } from "../api/profile";

/**
 * The browser gate (decision 8 Aug 2026): the web build shows every tab the
 * iOS app has, but only the Home overview and the Learn feed actually work.
 * Everything else renders this — the pitch for the full app, with the
 * waitlist as the action. The web surface is a funnel, not a second product.
 */

export function AppGate() {
  return (
    <div className="onb onb--hero">
      <div className="onb-hero">
        <h1 className="t-title onb-hero__title">This lives in the full app.</h1>
        <p className="onb-hero__sub">
          What you're looking at is the preview — your dashboard and your Learn feed. The rest ships
          with the app.
        </p>
        <WaitlistInline />
      </div>
    </div>
  );
}

/** Email capture + per-school rank, shared by the gate and the Learn feed's
 * CTA cards. Same flow, same row, same rank — wherever it's rendered. No
 * Telegram link on any web surface (decision 9 Aug 2026). */
export function WaitlistInline() {
  const [entry, setEntry] = useState<WaitlistEntry | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    void fetchWaitlist().then((existing) => {
      setEntry(existing);
      setLoaded(true);
    });
  }, []);

  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email.trim());
  const join = () => {
    if (!emailOk || submitting) return;
    setSubmitting(true);
    setFailed(false);
    void joinWaitlist(email.trim()).then((result) => {
      setSubmitting(false);
      if (result) setEntry(result);
      else setFailed(true);
    });
  };

  if (!loaded) return null;

  return (
    <>
      {entry ? (
        <p className="onb-hero__sub">
          You're #{entry.rank} at {entry.school}. I'll email you the second the app drops.
        </p>
      ) : (
        <>
          <input
            className="field onb-field"
            type="email"
            inputMode="email"
            autoComplete="email"
            placeholder="you@school.edu"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") join();
            }}
          />
          <button
            type="button"
            className="btn btn--primary"
            disabled={!emailOk || submitting}
            onClick={join}
          >
            {submitting ? "Saving…" : "Let me in!"}
          </button>
          {failed ? (
            <p className="t-caption onb-hint">That didn't save — give it another tap.</p>
          ) : null}
        </>
      )}
    </>
  );
}
